import { EMPTY_KEYWORD_REGEXP } from "./Search.constants";
import type {
  FlagKeyword,
  HighlightArea,
  NormalizedKeyword,
  SingleKeyword,
  TextItem
} from "./Search.types";

// Sort the highlight elements by their positions
export const sortHighlightPosition = (a: HighlightArea, b: HighlightArea) => {
  // Compare the top values first
  if (a.top < b.top) {
    return -1;
  }
  if (a.top > b.top) {
    return 1;
  }
  // Then compare the left values
  if (a.left < b.left) {
    return -1;
  }
  if (a.left > b.left) {
    return 1;
  }
  return 0;
};

// `$&` means the whole matched string
const escapeRegExp = (input: string): string =>
  input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeFlagKeyword = (flagKeyword: FlagKeyword): NormalizedKeyword => {
  const source = flagKeyword.wholeWords
    ? ` ${flagKeyword.keyword} `
    : flagKeyword.keyword;
  const flags = flagKeyword.matchCase ? "g" : "gi";
  return {
    keyword: flagKeyword.keyword,
    regExp: new RegExp(escapeRegExp(source), flags),
    wholeWords: flagKeyword.wholeWords || false,
    matchCase: flagKeyword.matchCase || false
  };
};

export const normalizeSingleKeyword = (
  keyword: SingleKeyword,
  matchCase?: boolean,
  wholeWords?: boolean
): NormalizedKeyword => {
  if (keyword instanceof RegExp) {
    return {
      keyword: keyword.source,
      regExp: keyword,
      wholeWords: wholeWords || false,
      matchCase: matchCase || false
    };
  }

  // Normalize a string keyword
  if (typeof keyword === "string") {
    return keyword === ""
      ? EMPTY_KEYWORD_REGEXP
      : normalizeFlagKeyword({
          keyword,
          matchCase: matchCase || false,
          wholeWords: wholeWords || false
        });
  }

  // Normalize a keyword with flags
  if (typeof matchCase !== "undefined") {
    keyword.matchCase = matchCase;
  }
  if (typeof wholeWords !== "undefined") {
    keyword.wholeWords = wholeWords;
  }
  return normalizeFlagKeyword(keyword);
};

/**
 * Finds the text items that contain the search text and returns their indices and positions irrespective of missing punctuations
 * @param textItems - Array of text items from PDF.js
 * @param searchText - The text to search for
 * @returns Array of matched items with their indices and substring positions
 * Handles broken text with missing punctuation and spaces
 */

export const findTextItemRanges = (
  textItems: TextItem[],
  searchText: string
): {
  itemIndex: number;
  item: TextItem;
  matchStartInItem: number;
  matchEndInItem: number;
  isPartialMatch: boolean;
}[] => {
  const results: {
    itemIndex: number;
    item: TextItem;
    matchStartInItem: number;
    matchEndInItem: number;
    isPartialMatch: boolean;
  }[] = [];

  const validItems = textItems.filter(
    item => "str" in item && item.str.trim() !== ""
  );

  if (validItems.length === 0 || !searchText) {
    return results;
  }

  // Normalize text: remove punctuation, spaces, lowercase
  const normalizeText = (text: string) =>
    text
      .replace(/[.,;:!?'"`’”|()[\]{}]/g, "")
      .replace(/\s+/g, "")
      .toLowerCase();

  const normalizedSearch = normalizeText(searchText);

  // Try to find match starting from each text item
  for (let startIdx = 0; startIdx < validItems.length; startIdx++) {
    const match = findMatchStartingFrom({
      items: validItems,
      startIdx,
      normalizedSearch,
      normalizeText
    });

    if (match.length > 0) {
      // Found a match! Map to original text item indices
      match.forEach(matchItem => {
        const originalIndex = textItems.findIndex(
          ti => "str" in ti && ti.str === matchItem.item.str
        );

        results.push({
          itemIndex: originalIndex,
          item: textItems[originalIndex],
          matchStartInItem: matchItem.matchStartInItem,
          matchEndInItem: matchItem.matchEndInItem,
          isPartialMatch:
            matchItem.matchStartInItem > 0 ||
            matchItem.matchEndInItem < matchItem.item.str.length
        });
      });

      return results; // Return first match
    }
  }

  return results;
};

/**
 * Attempts to find a complete match for a search string starting from a specific text item index.
 *
 * @param items - Array of text items to search through
 * @param startIdx - Index in the items array to begin searching from
 * @param normalizedSearch - The search string that has been normalized (e.g., lowercased, diacritics removed)
 * @param normalizeText - Function to normalize individual characters for comparison
 *
 * @returns An array of matched text items with their match positions. Each object contains:
 *   - `item`: The TextItem that contains part of the match
 *   - `matchStartInItem`: Character index where the match starts in this item (inclusive)
 *   - `matchEndInItem`: Character index where the match ends in this item (exclusive)
 *   Returns an empty array if no complete match is found.
 *
 * @remarks
 * - The function skips punctuation and whitespace characters: `.,;:!?'"\`'"|()[]{}` and `\s`
 * - A match is only considered successful if the entire normalized search string is consumed
 * - If a character doesn't match, the entire match attempt fails immediately (returns empty array)
 * - The match can span multiple consecutive text items
 */
const findMatchStartingFrom = ({
  items,
  startIdx,
  normalizedSearch,
  normalizeText
}: {
  items: TextItem[];
  startIdx: number;
  normalizedSearch: string;
  normalizeText: (text: string) => string;
}): {
  item: TextItem;
  matchStartInItem: number;
  matchEndInItem: number;
}[] => {
  const matchedItems: {
    item: TextItem;
    matchStartInItem: number;
    matchEndInItem: number;
  }[] = [];

  let searchPos = 0; // Position in normalized search string
  let currentItemIdx = startIdx;

  // Try to consume the entire search string
  while (searchPos < normalizedSearch.length && currentItemIdx < items.length) {
    const item = items[currentItemIdx];
    const itemText = item.str;

    // Find where in this item we can start matching
    let matchStartInItem = -1;
    let matchEndInItem = -1;

    // Walk through each character in the current text item
    for (
      let i = 0;
      i < itemText.length && searchPos < normalizedSearch.length;
      i++
    ) {
      const char = itemText[i];

      // Skip punctuation and spaces in the original text
      if (/[.,;:!?'"`’”|()[\]{}\s]/.test(char)) {
        continue;
      }

      const normalizedChar = normalizeText(char);

      // Check if this character matches the next character we're looking for
      if (normalizedChar !== normalizedSearch[searchPos]) {
        // Character doesn't match - this starting position doesn't work
        return []; // Fail this attempt
      }

      if (matchStartInItem === -1) {
        matchStartInItem = i; // Mark start of match in this item
      }

      matchEndInItem = i + 1; // Update end (exclusive)
      searchPos++; // Move forward in search string
    }

    // If we matched any characters in this item, record it
    if (matchStartInItem !== -1) {
      matchedItems.push({
        item,
        matchStartInItem,
        matchEndInItem
      });
    }

    // Move to next item to continue matching
    currentItemIdx++;
  }

  // Success only if we consumed the entire search string
  if (searchPos === normalizedSearch.length) {
    return matchedItems;
  }

  // Didn't match the full search string
  return [];
};
